import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListAnalyticsHelperService } from '@app/lists/services/list-analytics-helper.service';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  AllInvoices,
  PaymentActivityInvoice,
} from '@payments/services/file-handler-payments.service';
import { desiredColumn } from '@shared/models/desired-column';
import { SeperatedColumn } from '@shared/models/seperated-columns';
import { SentenceCasePipe } from '@shared/pipes/sentence-case.pipe';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { DownloadPrintBaseService } from '../download-print-base.service';
import {
  AllInvoicesColumns,
  PaymentActivityColumns,
} from '../helpers/document-helper-models-and-enums';
import { documentFunctionMap } from '../helpers/download-list-helper';
import {
  allInvoiceExcelToBlob,
  excelToBlob,
} from '../helpers/payment-activity-excel-helper';

@Injectable({
  providedIn: 'root',
})
export class DownloadPaymentActivityService extends DownloadPrintBaseService {
  constructor(
    http: HttpClient,
    toastService: ToastService,
    translateService: TranslateService,
    platform: Platform,
    datePipe: DatePipe,
    loadingSpinnerService: LoadingSpinnerService,
    listAnalyticsHelperService: ListAnalyticsHelperService,
    sentenceCasePipe: SentenceCasePipe,
    messageStoreService: MessageStoreService,
  ) {
    super(
      http,
      toastService,
      translateService,
      platform,
      datePipe,
      loadingSpinnerService,
      listAnalyticsHelperService,
      sentenceCasePipe,
      messageStoreService,
    );
  }

  generateAndSaveExcel(fileName: string, invoices: PaymentActivityInvoice[]) {
    const columnConfig = this.getInvoiceColumns();
    const seperatedColumns = this.createMultipleSeperatedColumnsForInvoices(
      invoices,
      columnConfig,
    );

    const excel = excelToBlob(
      fileName,
      seperatedColumns,
      columnConfig,
      invoices,
    );
    this.saveDocument(excel, fileName, 'XLSX', true);

    this.cleanUp();
  }

  generateAndSaveExcelForAllInvoices(
    fileName: string,
    invoices: AllInvoices[],
    showOrderNumber: boolean,
  ) {
    const columnConfig = this.getAllInvoiceColumns(showOrderNumber);
    const seperatedColumns = this.createMultipleSeperatedColumnsForAllInvoices(
      invoices,
      columnConfig,
    );

    const excel = allInvoiceExcelToBlob(
      seperatedColumns,
      columnConfig,
      invoices,
      showOrderNumber,
    );
    this.saveDocument(excel, fileName, 'XLSX', true);

    this.cleanUp();
  }

  private createMultipleSeperatedColumnsForInvoices(
    invoices: PaymentActivityInvoice[],
    columnConfig: desiredColumn[],
  ): SeperatedColumn[] {
    let columnMap = new Map<string, SeperatedColumn>();

    invoices.forEach(invoice => {
      columnConfig.forEach(column => {
        columnMap = documentFunctionMap[column.columnType](
          column.columnName,
          columnMap,
          invoice,
        );
      });
    });

    let seperatedColumns: SeperatedColumn[] = [];
    columnMap.forEach(column => {
      seperatedColumns.push(column);
    });

    return seperatedColumns;
  }

  private createMultipleSeperatedColumnsForAllInvoices(
    invoices: AllInvoices[],
    columnConfig: desiredColumn[],
  ): SeperatedColumn[] {
    let columnMap = new Map<string, SeperatedColumn>();

    invoices.forEach(invoice => {
      columnConfig.forEach(column => {
        columnMap = documentFunctionMap[column.columnType](
          column.columnName,
          columnMap,
          invoice,
        );
      });
    });

    let seperatedColumns: SeperatedColumn[] = [];
    columnMap.forEach(column => {
      seperatedColumns.push(column);
    });

    return seperatedColumns;
  }

  private getInvoiceColumns(): desiredColumn[] {
    const seperatedColumns: desiredColumn[] = [];

    seperatedColumns.push(
      {
        columnName: 'Customer Name',
        columnType: PaymentActivityColumns.customerName,
        cellWidth: 20,
      },
      {
        columnName: 'Customer Number',
        columnType: PaymentActivityColumns.customerNumber,
      },
      {
        columnName: 'Document Date',
        columnType: PaymentActivityColumns.documentDate,
        cellType: 'd',
        cellFormat: 'm/d/yyyy',
      },
      {
        columnName: 'Primary Transaction Number',
        columnType: PaymentActivityColumns.primaryTransactionNumber,
      },
      {
        columnName: 'Primary Transaction Type',
        columnType: PaymentActivityColumns.primaryTransactionType,
      },
      {
        columnName: 'Secondary Transaction Number',
        columnType: PaymentActivityColumns.secondaryTransactionNumber,
      },
      {
        columnName: 'Secondary Transaction Type',
        columnType: PaymentActivityColumns.secondaryTransactionType,
      },
      {
        columnName: 'Reference Number',
        columnType: PaymentActivityColumns.referenceNumber,
      },
      {
        columnName: 'Payment Method',
        columnType: PaymentActivityColumns.paymentMethod,
      },
      {
        columnName: 'Paid Amount',
        columnType: PaymentActivityColumns.paidAmount,
        cellType: 'n',
        cellFormat: '.00',
      },
      {
        columnName: 'Payment Date',
        columnType: PaymentActivityColumns.paymentDate,
        cellType: 'd',
        cellFormat: 'm/d/yyyy',
      },
      {
        columnName: 'Access',
        columnType: PaymentActivityColumns.access,
      },
    );

    return seperatedColumns;
  }

  private getAllInvoiceColumns(showOrderNumber: boolean): desiredColumn[] {
    const seperatedColumns: desiredColumn[] = [];

    seperatedColumns.push(
      {
        columnName: 'Customer Name',
        columnType: AllInvoicesColumns.customerName,
        cellWidth: 20,
      },
      {
        columnName: 'Customer Number',
        columnType: AllInvoicesColumns.customerNumber,
      },
      {
        columnName: 'Primary Transaction Type',
        columnType: AllInvoicesColumns.primaryTransactionType,
      },
      {
        columnName: 'Primary Transaction Number',
        columnType: AllInvoicesColumns.primaryTransactionNumber,
      },
      {
        columnName: 'Secondary Transaction Type',
        columnType: AllInvoicesColumns.secondaryTransactionType,
      },
      {
        columnName: 'Secondary Transaction Number',
        columnType: AllInvoicesColumns.secondaryTransactionNumber,
      },
      {
        columnName: 'Cust PO',
        columnType: AllInvoicesColumns.customerPO,
        cellWidth: 20,
      },
      ...(showOrderNumber
        ? [
            {
              columnName: 'Order Number',
              columnType: AllInvoicesColumns.orderNumber,
              cellWidth: 20,
            },
          ]
        : []),

      {
        columnName: 'Date Issued',
        columnType: AllInvoicesColumns.dateIssued,
      },
      {
        columnName: 'Amount',
        columnType: AllInvoicesColumns.amount,
      },
      {
        columnName: 'Amount Due',
        columnType: AllInvoicesColumns.amountDue,
      },
      {
        columnName: 'Due Date',
        columnType: AllInvoicesColumns.dueDate,
      },
      {
        columnName: 'Status',
        columnType: AllInvoicesColumns.status,
      },
    );

    return seperatedColumns;
  }
}
